import { ActionSheetContext, Column, Link, Row, XxsSpacing } from '@alto/design-system';
import { InsuranceDeletionActionSheet } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext } from 'react';
import { selectInsurance } from '~shared/actions/insurances';
import { type InsuranceSource } from '~shared/constants';
import getIsPhotoUpload from '~shared/features/insurances/helpers/getIsPhotoUpload';
import { getSelectedUserID } from '~shared/features/ui/selectors/getUsers';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Insurance as InsuranceType } from '~shared/types';
import InsuranceCard from '~web/features/onboarding/components/redesign/InsuranceCard';

type Props = {
  readonly hideActions?: boolean;
  readonly insurance: InsuranceType;
  readonly source: InsuranceSource;
  readonly onEditInsurance?: () => void; // only used for organic onboarding
};

export const Insurance = ({ hideActions = false, insurance, onEditInsurance, source }: Props) => {
  const dispatch = useDispatchShared();
  const { navigate } = useNavigation();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const currentUserID = useSelectorShared(getCurrentUserID);
  const selectedUserID = useSelectorShared(getSelectedUserID);

  const handleEditInsurance = () => {
    const userID = selectedUserID || currentUserID;
    dispatch(selectInsurance(insurance));
    if (!userID) return;
    if (source === 'onboarding' && onEditInsurance) {
      onEditInsurance();
    } else {
      navigate('RouteInsuranceForm', { insuranceID: insurance.id, source, userID });
    }
  };

  const handleDeleteInsurance = () => {
    dispatch(selectInsurance(insurance));
    setActiveActionSheet(
      <InsuranceDeletionActionSheet
        insurance={insurance}
        source={source}
      />,
    );
  };

  const insuranceStillProcessing = getIsPhotoUpload(insurance);

  return (
    <Column gap="XXS">
      <InsuranceCard insurance={insurance} />
      <XxsSpacing key="edit insurance spacing" />
      {!hideActions && (
        <Row
          right
          centerVertically
        >
          {!insuranceStillProcessing && (
            <>
              <Link
                textSize="mini"
                onPress={handleEditInsurance}
              >
                Edit
              </Link>
              <span>&nbsp;|&nbsp;</span>
            </>
          )}
          <>
            <Link
              textSize="mini"
              onPress={handleDeleteInsurance}
            >
              Delete
            </Link>
          </>
        </Row>
      )}
    </Column>
  );
};
